import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  People as PeopleIcon,
  Work as WorkIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import { 
  getAdminStats, 
  getUsers, 
  getProjects, 
  getOffers, 
  getCreditTransactions,
  AdminStats,
  UserProfile,
  ProjectData,
  OfferData,
  CreditTransaction,
  updateUserStatus,
  deleteUser,
  updateProjectStatus,
  deleteProject
} from '../lib/api/admin';
import { 
  getVerificationRequests, 
  approveVerification, 
  rejectVerification,
  getVerificationStats,
  VerificationRequestWithUser,
} from '../lib/api/verification';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Stats
  const [stats, setStats] = useState<AdminStats | null>(null);
  
  // Users
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [usersPage] = useState(0);
  const [userFilter, setUserFilter] = useState({ role: '', status: '', search: '' });
  
  // Projects
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [projectsPage] = useState(0);
  const [projectFilter, setProjectFilter] = useState({ status: '', search: '' });
  
  // Offers
  const [offers, setOffers] = useState<OfferData[]>([]);
  const [offersPage] = useState(0);
  
  // Credit Transactions
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [transactionsPage] = useState(0);
  
  // Verifications
  const [verifications, setVerifications] = useState<VerificationRequestWithUser[]>([]);
  const [verificationsPage] = useState(0);
  const [verificationFilter, setVerificationFilter] = useState({ status: '', search: '' });
  
  // UI State
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [selectedProject, setSelectedProject] = useState<ProjectData | null>(null);
  const [selectedVerification, setSelectedVerification] = useState<VerificationRequestWithUser | null>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [projectMenuAnchor, setProjectMenuAnchor] = useState<null | HTMLElement>(null);
  const [verificationMenuAnchor, setVerificationMenuAnchor] = useState<null | HTMLElement>(null);
  const [verificationRejectDialog, setVerificationRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteType, setDeleteType] = useState<'user' | 'project' | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 0) {
      loadUsers();
    } else if (activeTab === 1) {
      loadProjects();
    } else if (activeTab === 2) {
      loadOffers();
    } else if (activeTab === 3) {
      loadTransactions();
    } else if (activeTab === 4) {
      loadVerifications();
    }
  }, [activeTab, userFilter, projectFilter, verificationFilter]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [statsResult, usersResult, verificationStatsResult] = await Promise.all([
        getAdminStats(),
        getUsers({ page: 0, limit: 10 }),
        getVerificationStats()
      ]);

      if (statsResult.error) {
        setError(statsResult.error);
        return;
      }

      if (usersResult.error) {
        setError(usersResult.error);
        return;
      }

      if (verificationStatsResult.error) {
        console.error('Error loading verification stats:', verificationStatsResult.error);
      }

      setStats(statsResult.data);
      setUsers(usersResult.data);
    } catch (err) {
      setError('Неочаквана грешка при зареждане на данните');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const result = await getUsers({ 
        page: usersPage, 
        limit: 10, 
        ...userFilter 
      });
      
      if (result.error) {
        setError(result.error);
        return;
      }

      setUsers(result.data);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadProjects = async () => {
    try {
      const result = await getProjects({ 
        page: projectsPage, 
        limit: 10, 
        ...projectFilter 
      });
      
      if (result.error) {
        setError(result.error);
        return;
      }

      setProjects(result.data);
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  };

  const loadOffers = async () => {
    try {
      const result = await getOffers({ 
        page: offersPage, 
        limit: 10 
      });
      
      if (result.error) {
        setError(result.error);
        return;
      }

      setOffers(result.data);
    } catch (err) {
      console.error('Error loading offers:', err);
    }
  };

  const loadTransactions = async () => {
    try {
      const result = await getCreditTransactions({ 
        page: transactionsPage, 
        limit: 10 
      });
      
      if (result.error) {
        setError(result.error);
        return;
      }

      setTransactions(result.data);
    } catch (err) {
      console.error('Error loading transactions:', err);
    }
  };

  const loadVerifications = async () => {
    try {
      const result = await getVerificationRequests(
        verificationFilter.status || undefined,
        10,
        verificationsPage * 10
      );
      
      if (result.error) {
        setError(result.error);
        return;
      }

      setVerifications(result.data);
    } catch (err) {
      console.error('Error loading verifications:', err);
    }
  };

  const handleUserAction = async (action: string, user: UserProfile) => {
    setSelectedUser(user);
    
    switch (action) {
      case 'approve':
        const approveResult = await updateUserStatus(user.id, 'ACTIVE');
        if (approveResult.success) {
          loadUsers();
        } else {
          setError(approveResult.error || 'Грешка при одобряване');
        }
        break;
      case 'suspend':
        // TODO: Implement user status dialog
        break;
      case 'changeRole':
        // TODO: Implement user role dialog
        break;
      case 'delete':
        setDeleteType('user');
        setDeleteDialog(true);
        break;
    }
    
    setUserMenuAnchor(null);
  };

  const handleProjectAction = async (action: string, project: ProjectData) => {
    setSelectedProject(project);
    
    switch (action) {
      case 'publish':
        const publishResult = await updateProjectStatus(project.id, 'PUBLISHED');
        if (publishResult.success) {
          loadProjects();
        } else {
          setError(publishResult.error || 'Грешка при публикуване');
        }
        break;
      case 'close':
        // TODO: Implement project status dialog
        break;
      case 'delete':
        setDeleteType('project');
        setDeleteDialog(true);
        break;
    }
    
    setProjectMenuAnchor(null);
  };

  const handleVerificationAction = async (action: string, verification: VerificationRequestWithUser) => {
    setSelectedVerification(verification);
    
    switch (action) {
      case 'approve':
        const approveResult = await approveVerification(verification.id);
        if (approveResult.success) {
          loadVerifications();
        } else {
          setError(approveResult.error || 'Грешка при одобряване');
        }
        break;
      case 'reject':
        setVerificationRejectDialog(true);
        break;
    }
    
    setVerificationMenuAnchor(null);
  };

  const handleRejectVerification = async () => {
    if (!selectedVerification || !rejectionReason.trim()) return;

    try {
      const result = await rejectVerification(
        selectedVerification.id,
        rejectionReason,
        adminNotes || undefined
      );

      if (result.success) {
        loadVerifications();
        setVerificationRejectDialog(false);
        setRejectionReason('');
        setAdminNotes('');
        setSelectedVerification(null);
      } else {
        setError(result.error || 'Грешка при отхвърляне');
      }
    } catch (err) {
      setError('Неочаквана грешка при отхвърляне');
    }
  };

  const handleDelete = async () => {
    if (!deleteType || (!selectedUser && !selectedProject)) return;

    try {
      let result;
      if (deleteType === 'user' && selectedUser) {
        result = await deleteUser(selectedUser.id);
      } else if (deleteType === 'project' && selectedProject) {
        result = await deleteProject(selectedProject.id);
      }

      if (result?.success) {
        if (deleteType === 'user') {
          loadUsers();
        } else if (deleteType === 'project') {
          loadProjects();
        }
        setDeleteDialog(false);
        setDeleteType(null);
        setSelectedUser(null);
        setSelectedProject(null);
      } else {
        setError(result?.error || 'Грешка при изтриване');
      }
    } catch (err) {
      setError('Неочаквана грешка при изтриване');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PENDING_VERIFICATION': return 'warning';
      case 'SUSPENDED': return 'error';
      case 'PUBLISHED': return 'success';
      case 'DRAFT': return 'default';
      case 'PENDING': return 'warning';
      case 'CLOSED': return 'info';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'error';
      case 'CLIENT': return 'primary';
      case 'FREELANCER': return 'success';
      case 'AGENCY': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
        <Header />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress sx={{ color: '#28A0F0' }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <Header />
      
      <Container maxWidth="xl" sx={{ py: 4, width: '80%' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: '#ffffff',
                  mb: 1,
                }}
              >
                Админ Панел
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#a0a0a0',
                }}
              >
                Управление на платформата и потребители
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="white" variant="h4" sx={{ fontWeight: 700 }}>
                        {stats.totalUsers}
                      </Typography>
                      <Typography color="white" variant="body2">
                        Общо потребители
                      </Typography>
                    </Box>
                    <PeopleIcon sx={{ color: 'white', fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="white" variant="h4" sx={{ fontWeight: 700 }}>
                        {stats.totalProjects}
                      </Typography>
                      <Typography color="white" variant="body2">
                        Общо проекти
                      </Typography>
                    </Box>
                    <WorkIcon sx={{ color: 'white', fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="white" variant="h4" sx={{ fontWeight: 700 }}>
                        {stats.pendingVerifications}
                      </Typography>
                      <Typography color="white" variant="body2">
                        Чакащи одобрение
                      </Typography>
                    </Box>
                    <Badge badgeContent={stats.pendingVerifications} color="error">
                      <CheckIcon sx={{ color: 'white', fontSize: 40 }} />
                    </Badge>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="white" variant="h4" sx={{ fontWeight: 700 }}>
                        {stats.totalEarnings.toFixed(0)} лв.
                      </Typography>
                      <Typography color="white" variant="body2">
                        Общо приходи
                      </Typography>
                    </Box>
                    <MoneyIcon sx={{ color: 'white', fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tabs */}
        <Paper sx={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                color: '#a0a0a0',
                '&.Mui-selected': {
                  color: '#28A0F0',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#28A0F0',
              },
            }}
          >
            <Tab label="Users" />
            <Tab label="Projects" />
            <Tab label="Offers" />
            <Tab label="Transactions" />
            <Tab label="Verifications" />
          </Tabs>

          {/* Users Tab */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                placeholder="Search users..."
                value={userFilter.search}
                onChange={(e) => setUserFilter(prev => ({ ...prev, search: e.target.value }))}
                size="small"
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={userFilter.role}
                  onChange={(e) => setUserFilter(prev => ({ ...prev, role: e.target.value }))}
                  label="Role"
                >
                  <MenuItem value="">Всички</MenuItem>
                  <MenuItem value="CLIENT">Клиенти</MenuItem>
                  <MenuItem value="FREELANCER">Фриленсъри</MenuItem>
                  <MenuItem value="AGENCY">Агенции</MenuItem>
                  <MenuItem value="ADMIN">Администратори</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Статус</InputLabel>
                <Select
                  value={userFilter.status}
                  onChange={(e) => setUserFilter(prev => ({ ...prev, status: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="">Всички</MenuItem>
                  <MenuItem value="ACTIVE">Активни</MenuItem>
                  <MenuItem value="PENDING_VERIFICATION">Чакащи</MenuItem>
                  <MenuItem value="SUSPENDED">Спряни</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadUsers}
                sx={{ ml: 'auto' }}
              >
                Обнови
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Име</TableCell>
                    <TableCell>Имейл</TableCell>
                    <TableCell>Роля</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Регистриран</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500 }}>
                            {user.full_name}
                          </Typography>
                          {user.company && (
                            <Typography variant="caption" sx={{ color: '#a0a0a0' }}>
                              {user.company}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: '#a0a0a0' }}>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          color={getRoleColor(user.role) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.status}
                          color={getStatusColor(user.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#a0a0a0' }}>
                        {new Date(user.created_at).toLocaleDateString('bg-BG')}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => {
                            setUserMenuAnchor(e.currentTarget);
                            setSelectedUser(user);
                          }}
                          sx={{ color: '#a0a0a0' }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Projects Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                placeholder="Search projects..."
                value={projectFilter.search}
                onChange={(e) => setProjectFilter(prev => ({ ...prev, search: e.target.value }))}
                size="small"
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Статус</InputLabel>
                <Select
                  value={projectFilter.status}
                  onChange={(e) => setProjectFilter(prev => ({ ...prev, status: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="">Всички</MenuItem>
                  <MenuItem value="DRAFT">Чернова</MenuItem>
                  <MenuItem value="PENDING">Чакащи</MenuItem>
                  <MenuItem value="PUBLISHED">Публикувани</MenuItem>
                  <MenuItem value="CLOSED">Затворени</MenuItem>
                  <MenuItem value="CANCELLED">Отказани</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadProjects}
                sx={{ ml: 'auto' }}
              >
                Обнови
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Заглавие</TableCell>
                    <TableCell>Клиент</TableCell>
                    <TableCell>Бюджет</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Оферти</TableCell>
                    <TableCell>Създаден</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projects.map((project) => (
                    <TableRow key={project.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500 }}>
                          {project.title}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: '#a0a0a0' }}>
                        {project.client_name}
                        {project.client_company && ` (${project.client_company})`}
                      </TableCell>
                      <TableCell sx={{ color: '#a0a0a0' }}>
                        {project.budget_min && project.budget_max
                          ? `${project.budget_min} - ${project.budget_max} лв.`
                          : 'Не е посочен'
                        }
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={project.status}
                          color={getStatusColor(project.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#a0a0a0' }}>
                        {project.offers_count}
                      </TableCell>
                      <TableCell sx={{ color: '#a0a0a0' }}>
                        {new Date(project.created_at).toLocaleDateString('bg-BG')}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => {
                            setProjectMenuAnchor(e.currentTarget);
                            setSelectedProject(project);
                          }}
                          sx={{ color: '#a0a0a0' }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Offers Tab */}
          <TabPanel value={activeTab} index={2}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Проект</TableCell>
                    <TableCell>Изпълнител</TableCell>
                    <TableCell>Цена</TableCell>
                    <TableCell>Срок</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Създадена</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {offers.map((offer) => (
                    <TableRow key={offer.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500 }}>
                          {offer.project_title}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: '#a0a0a0' }}>
                        {offer.provider_name}
                        {offer.provider_company && ` (${offer.provider_company})`}
                      </TableCell>
                      <TableCell sx={{ color: '#a0a0a0' }}>
                        {offer.price} лв.
                      </TableCell>
                      <TableCell sx={{ color: '#a0a0a0' }}>
                        {offer.delivery_days} дни
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={offer.status}
                          color={getStatusColor(offer.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#a0a0a0' }}>
                        {new Date(offer.created_at).toLocaleDateString('bg-BG')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Transactions Tab */}
          <TabPanel value={activeTab} index={3}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Потребител</TableCell>
                    <TableCell>Тип</TableCell>
                    <TableCell>Сума</TableCell>
                    <TableCell>Описание</TableCell>
                    <TableCell>Пакет</TableCell>
                    <TableCell>Дата</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell sx={{ color: '#a0a0a0' }}>
                        {transaction.user_name}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.type}
                          color={transaction.amount > 0 ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ color: transaction.amount > 0 ? '#10b981' : '#ef4444' }}>
                        {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                      </TableCell>
                      <TableCell sx={{ color: '#a0a0a0' }}>
                        {transaction.description || '-'}
                      </TableCell>
                      <TableCell sx={{ color: '#a0a0a0' }}>
                        {transaction.package_name || '-'}
                      </TableCell>
                      <TableCell sx={{ color: '#a0a0a0' }}>
                        {new Date(transaction.created_at).toLocaleDateString('bg-BG')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Verifications Tab */}
          <TabPanel value={activeTab} index={4}>
            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                placeholder="Search verifications..."
                value={verificationFilter.search}
                onChange={(e) => setVerificationFilter(prev => ({ ...prev, search: e.target.value }))}
                size="small"
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Статус</InputLabel>
                <Select
                  value={verificationFilter.status}
                  onChange={(e) => setVerificationFilter(prev => ({ ...prev, status: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="">Всички</MenuItem>
                  <MenuItem value="PENDING">Чакащи</MenuItem>
                  <MenuItem value="APPROVED">Одобрени</MenuItem>
                  <MenuItem value="REJECTED">Отхвърлени</MenuItem>
                  <MenuItem value="EXPIRED">Изтекли</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadVerifications}
                sx={{ ml: 'auto' }}
              >
                Обнови
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Потребител</TableCell>
                    <TableCell>Кодова фраза</TableCell>
                    <TableCell>Профили</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Подадена</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {verifications.map((verification) => (
                    <TableRow key={verification.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500 }}>
                            {verification.user_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#a0a0a0' }}>
                            {verification.user_email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: '#a0a0a0', fontFamily: 'monospace' }}>
                        {verification.verification_code}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {verification.linkedin_url && (
                            <Typography variant="caption" sx={{ color: '#a0a0a0' }}>
                              LinkedIn: {verification.linkedin_url.length > 30 ? 
                                verification.linkedin_url.substring(0, 30) + '...' : 
                                verification.linkedin_url
                              }
                            </Typography>
                          )}
                          {verification.website_url && (
                            <Typography variant="caption" sx={{ color: '#a0a0a0' }}>
                              Website: {verification.website_url.length > 30 ? 
                                verification.website_url.substring(0, 30) + '...' : 
                                verification.website_url
                              }
                            </Typography>
                          )}
                          {verification.portfolio_url && (
                            <Typography variant="caption" sx={{ color: '#a0a0a0' }}>
                              Portfolio: {verification.portfolio_url.length > 30 ? 
                                verification.portfolio_url.substring(0, 30) + '...' : 
                                verification.portfolio_url
                              }
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={verification.status}
                          color={getStatusColor(verification.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#a0a0a0' }}>
                        {new Date(verification.submitted_at).toLocaleDateString('bg-BG')}
                      </TableCell>
                      <TableCell>
                        {verification.status === 'PENDING' && (
                          <IconButton
                            onClick={(e) => {
                              setVerificationMenuAnchor(e.currentTarget);
                              setSelectedVerification(verification);
                            }}
                            sx={{ color: '#a0a0a0' }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </Paper>
      </Container>

      {/* User Actions Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
      >
        {selectedUser?.status === 'PENDING_VERIFICATION' && (
          <MenuItem onClick={() => handleUserAction('approve', selectedUser)}>
            <CheckIcon sx={{ mr: 1 }} />
            Одобри
          </MenuItem>
        )}
        <MenuItem onClick={() => handleUserAction('suspend', selectedUser!)}>
          <CancelIcon sx={{ mr: 1 }} />
          Спри/Активирай
        </MenuItem>
        <MenuItem onClick={() => handleUserAction('changeRole', selectedUser!)}>
          <EditIcon sx={{ mr: 1 }} />
          Смени роля
        </MenuItem>
        <MenuItem onClick={() => handleUserAction('delete', selectedUser!)} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Изтрий
        </MenuItem>
      </Menu>

      {/* Project Actions Menu */}
      <Menu
        anchorEl={projectMenuAnchor}
        open={Boolean(projectMenuAnchor)}
        onClose={() => setProjectMenuAnchor(null)}
      >
        {selectedProject?.status === 'PENDING' && (
          <MenuItem onClick={() => handleProjectAction('publish', selectedProject)}>
            <CheckIcon sx={{ mr: 1 }} />
            Публикувай
          </MenuItem>
        )}
        <MenuItem onClick={() => handleProjectAction('close', selectedProject!)}>
          <CancelIcon sx={{ mr: 1 }} />
          Затвори
        </MenuItem>
        <MenuItem onClick={() => handleProjectAction('delete', selectedProject!)} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Изтрий
        </MenuItem>
      </Menu>

      {/* Verification Actions Menu */}
      <Menu
        anchorEl={verificationMenuAnchor}
        open={Boolean(verificationMenuAnchor)}
        onClose={() => setVerificationMenuAnchor(null)}
      >
        {selectedVerification?.status === 'PENDING' && (
          <>
            <MenuItem onClick={() => handleVerificationAction('approve', selectedVerification)}>
              <CheckIcon sx={{ mr: 1 }} />
              Одобри
            </MenuItem>
            <MenuItem onClick={() => handleVerificationAction('reject', selectedVerification)} sx={{ color: 'error.main' }}>
              <CancelIcon sx={{ mr: 1 }} />
              Отхвърли
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Потвърди изтриване</DialogTitle>
        <DialogContent>
          <Typography>
            Сигурни ли сте, че искате да изтриете {deleteType === 'user' ? 'потребителя' : 'проекта'}?
            Това действие не може да бъде отменено.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Отказ</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Изтрий
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verification Reject Dialog */}
      <Dialog open={verificationRejectDialog} onClose={() => setVerificationRejectDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Отхвърли верификация</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Моля, посочете причината за отхвърляне на верификацията на {selectedVerification?.user_name}:
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('admin.rejectionReason')}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            multiline
            rows={2}
            label={t('admin.adminNotes')}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder={t('admin.adminNotesPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerificationRejectDialog(false)}>Отказ</Button>
          <Button 
            onClick={handleRejectVerification} 
            color="error" 
            variant="contained"
            disabled={!rejectionReason.trim()}
          >
            Отхвърли
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AdminDashboard;
